import { useState } from "react";
import { ActionIcon, Button, Card, Group } from "@mantine/core";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";
import { idGenerator } from "generate-custom-id";
import { useRef } from "react";

import QuerySection from "../QuerySection/QuerySection";
import ConnectorAnchor from "../../connectors/ConnectorAnchor";
import {
  usePostSubQueryMutation,
  useDeleteSubQueryMutation,
} from "../../hooks/mutations";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import classes from "./SubQuery.module.scss";

export default function SubQuery({
  ruleId,
  queryData,
  allEntitiesData,
  canEdit = true,
  startPointId,
}) {
  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);
  const leftId = useRef(idGenerator("example", 4)).current;
  const rightId = useRef(idGenerator("example", 4)).current;

  const { mutate: postSubQuery } = usePostSubQueryMutation({ ruleId });
  const { mutate: deleteSubQuery } = useDeleteSubQueryMutation({ ruleId });

  const hasInnerQueries = !!queryData?.queries?.length;

  return (
    <Group align="flex-start" gap="xl" wrap="nowrap" className={classes.subqueryRow}>
      <Card
        className={classes.card}
        shadow="sm"
        padding="md"
        radius="md"
        withBorder
      >
        {/* Connector attachment points (invisible). The left anchor's
            from={startPointId} tells the ConnectorLayer to draw the
            incoming line from the parent's right anchor. */}
        <ConnectorAnchor id={leftId} from={startPointId} side="left" />
        {hasInnerQueries && <ConnectorAnchor id={rightId} side="right" />}

        <QuerySection
          ruleId={ruleId}
          query={queryData}
          allEntitiesData={allEntitiesData}
          canEdit={canEdit}
          isSubquery
          enableJoins
          headerRightSlot={
            canEdit && (
              <Group gap="xs">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => postSubQuery(queryData.id)}
                >
                  Subquery
                </Button>
                <ActionIcon
                  variant="outline"
                  color="red"
                  aria-label="Delete subquery"
                  onClick={() => setConfirmDeleteOpened(true)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            )
          }
        />
      </Card>

      {/* Recursive children — laid out horizontally (to the right), never
          stacked vertically, so the whole query tree reads as one row. */}
      {hasInnerQueries && (
        <Group align="flex-start" gap="xl" wrap="nowrap" style={{ flexShrink: 0 }}>
          {queryData.queries.map((inner) => (
            <SubQuery
              key={inner.id}
              ruleId={ruleId}
              queryData={inner}
              allEntitiesData={allEntitiesData}
              canEdit={canEdit}
              startPointId={rightId}
            />
          ))}
        </Group>
      )}

      <DeleteConfirmModal
        opened={confirmDeleteOpened}
        onClose={() => setConfirmDeleteOpened(false)}
        onDelete={() => {
          deleteSubQuery(queryData.id);
          setConfirmDeleteOpened(false);
        }}
        label="Delete subquery?"
      />
    </Group>
  );
}
